import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    ArrowLeft, TrendingUp, TrendingDown, Minus, FileText, Mic, AlertCircle,
    Users, Clock, Building, ExternalLink, Calendar, BarChart2, ChevronRight, RefreshCw, Bookmark, BookmarkCheck, Briefcase, Target, Sparkles, Check, HelpCircle
} from 'lucide-react';

import api from '../../../api/axios';
import Navbaar from '../../../components/Navbar/Navbaar';
import Sidebar from '../../../components/Navbar/SideNavbar';
import { renderCompanyAvatar, getDaysAgo } from '../../../components/HiringSignalsPage/HSContent/HSCard';
import CompanyInsightsModal from '../../../components/HiringSignalsPage/CompanyInsightsModal';
import ContactCard from '../../../components/Referral/ContactCard';
import ContactsModal from '../../../components/Referral/ContactsModal';
import OutreachStrategyModal from '../../../components/Outreach/OutreachStrategyModal';
import { fetchRecruiterForJob, peekRecruiterForJob } from '../../../redux/JobSlice/jobSlice';
import { fetchCompanyInsights, peekCompanyPositioning } from '../../../redux/HSignalSlice/hsignalSlice';
import { activateSprint } from '../../../redux/ActiveSprintSlice/activeSprintSlice';
import { useNavbarActions, useNavbarTitle } from '../../../context/NavbarActionsContext';

/**
 * Everything a company has said or filed about its workforce.
 *
 * The board shows one line per company because a table row holds one. That
 * discards most of what was collected — ZoomInfo carries ten signals across four
 * sources, and the one that wins the headline is rarely the most useful. The
 * 10-K gives an audited number; only the earnings call says WHY; last year's
 * filing is what turns this year's number into a trend.
 *
 * So this page shows all of it, grouped by document, unfiltered by direction. A
 * company growing engineering while cutting support has two true things to say,
 * and a page devoted to that company is the wrong place to hide one of them.
 */

const SOURCE_META = {
    '10-K': { label: 'Annual report (10-K)', short: '10-K', icon: FileText, tone: 'bg-blue-50 text-blue-700 border-blue-200', why: 'Audited headcount, filed once a year. The SEC requires it.' },
    '10-Q': { label: 'Quarterly report (10-Q)', short: '10-Q', icon: FileText, tone: 'bg-sky-50 text-sky-700 border-sky-200', why: 'Quarterly filing. Headcount appears when it explains a change in costs.' },
    '8-K': { label: 'Material event (8-K)', short: '8-K', icon: AlertCircle, tone: 'bg-amber-50 text-amber-700 border-amber-200', why: 'Filed within days of a material event. Layoffs must be disclosed here by law.' },
    transcript: { label: 'Earnings call', short: 'Call', icon: Mic, tone: 'bg-violet-50 text-violet-700 border-violet-200', why: "An executive's own words to investors — the only source that gives a reason." },
};

const DIRECTION_META = {
    positive: { label: 'Hiring', icon: TrendingUp, tone: 'bg-emerald-100 text-emerald-700', bar: 'border-emerald-400' },
    negative: { label: 'Cutting', icon: TrendingDown, tone: 'bg-red-100 text-red-700', bar: 'border-red-400' },
    neutral: { label: 'Neutral', icon: Minus, tone: 'bg-gray-100 text-gray-600', bar: 'border-gray-300' },
    ambiguous: { label: 'Unclear', icon: Minus, tone: 'bg-gray-100 text-gray-600', bar: 'border-gray-300' },
};


/**
 * The window a headcount change was measured over.
 *
 * "+18.7%" is not interpretable on its own — strong growth over a year, modest
 * over three. Both endpoints are shown rather than just the duration, because a
 * reader checking against a filing needs the actual dates.
 */
const formatSpan = (from, to) => {
    if (!from || !to) return null;
    const a = new Date(from);
    const b = new Date(to);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;

    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    const months = Math.max(1, Math.round((b - a) / (30.44 * 86400000)));
    const label = months >= 12 && months % 12 === 0
        ? `${months / 12} yr`
        : months >= 18 ? `${(months / 12).toFixed(1)} yrs` : `${months} mo`;

    return { from: fmt(a), to: fmt(b), label };
};

/**
 * Loading state for the inline expansion.
 *
 * A single large spinner rather than a skeleton. The skeleton was drawing a
 * convincing hero, four tiles and a rail that the real page then replaced —
 * enough detail that it read as content and the swap felt like a flicker. One
 * clear "working" mark at a fixed height is calmer and never pretends to be
 * something it is not.
 */
const EmbeddedLoader = ({ ticker }) => (
    <div className="flex flex-col items-center justify-center gap-4" style={{ height: 300 }}>
        <div className="relative" style={{ width: 52, height: 52 }}>
            {/* Track, then the arc that turns on top of it. */}
            <div className="absolute inset-0 rounded-full border-[5px]" style={{ borderColor: '#E4E8F2' }} />
            <div className="absolute inset-0 rounded-full border-[5px] border-transparent animate-spin"
                 style={{ borderTopColor: '#3F57E0', borderRightColor: '#3F57E0', animationDuration: '.75s' }} />
        </div>
        <div className="text-center">
            <p className="text-[13px] font-semibold" style={{ color: '#334155' }}>Loading {ticker}</p>
            <p className="text-[11.5px] mt-1" style={{ color: '#94A3B8' }}>Filings, scores and contacts</p>
        </div>
    </div>
);

const DetailSkeleton = () => (
    <div className="px-6 py-6 space-y-4">
        <div className="h-28 rounded-2xl bg-gray-100 animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-gray-200 p-5 space-y-3">
                <div className="h-4 w-40 rounded bg-gray-200 animate-pulse" />
                <div className="h-3 w-full rounded bg-gray-100 animate-pulse" />
                <div className="h-3 w-4/5 rounded bg-gray-100 animate-pulse" />
            </div>
        ))}
    </div>
);

/**
 * One score as a ring.
 *
 * The arc is the number — a reader takes in three rings faster than three bars,
 * and the hero is the one place on this page worth spending that much height on.
 *
 * A null score draws an empty track and reads "—", never 0. Fewer than two
 * comparable attributes means we cannot rate the fit, and a zero-filled ring
 * would look like a verdict rather than an absence.
 */
const Dial = ({ score, label, hint, c, bg, size = 108 }) => {
    const pct = (score || 0) * 10;
    const r = (size - 14) / 2;
    const circ = 2 * Math.PI * r;
    const known = score != null;
    return (
        <div className="flex flex-col items-center text-center gap-2.5">
            <div className="relative" style={{ width: size, height: size }}>
                <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={bg} strokeWidth="11" />
                    {known && (
                        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={c} strokeWidth="11"
                                strokeLinecap="round" strokeDasharray={`${(circ * pct) / 100} ${circ}`} />
                    )}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-[30px] font-extrabold tracking-[-0.03em] leading-none"
                          style={{ color: known ? c : '#94A3B8' }}>
                        {known ? score : '—'}
                    </span>
                    <span className="text-[11px] font-bold mt-0.5" style={{ color: '#94A3B8' }}>/ 10</span>
                </div>
            </div>
            <div>
                <div className="text-[12.5px] font-extrabold tracking-tight" style={{ color: '#0F172A' }}>{label}</div>
                <div className="text-[11px] mt-[3px]" style={{ color: '#64748B' }}>{hint}</div>
            </div>
        </div>
    );
};

/*
 * Plain-language readings of each score, so the ring is not the only thing on
 * offer. Derived from the real number rather than hard-coded like the mock.
 */
const signalHint = (n) => n == null ? 'No hiring evidence'
    : n >= 8 ? 'Strong & recent' : n >= 6 ? 'Solid & recent' : n >= 4 ? 'Some evidence' : 'Thin evidence';
const fitHint = (n) => n == null ? 'Not enough to compare'
    : n >= 8 ? 'Strong match' : n >= 5 ? 'Partial match' : n >= 1 ? 'Weak match' : 'Not a match';
const overallHint = (n) => n == null ? 'Company only'
    : n >= 7 ? 'Worth pursuing' : n >= 4 ? 'Worth a look' : 'Low priority';

/*
 * Design tokens from the Career Champ canvas, so this page matches the rest of
 * the system rather than re-inventing greys per component.
 */
const INK = '#0F172A', INK7 = '#334155', MUT = '#64748B', INK4 = '#94A3B8';
const LINE = '#E6E8EE', DIV = '#EFF1F5';

/*
 * Surfaces.
 *
 * Every card used to be pure white on a #FCFCFC page — a two-percent difference,
 * so nothing read as a card at all and the whole page was one flat sheet. The
 * canvas now sits a few steps down and the surfaces carry a faint cast, which is
 * what gives an edge without needing a heavier border or a drop shadow.
 *
 * The rail tints come from the v2 design's section bands, at a fraction of their
 * saturation: enough that fit, contacts and related read as three different
 * kinds of thing, not so much that the page turns into a paint chart.
 */
const CANVAS = '#F4F5FA';
const SURFACE = '#FDFDFF';
const TONES = {
    plain:  { bg: SURFACE,   line: LINE },
    /*
     * Stat-row surfaces — a wash, not a fill.
     *
     * A flat tint made each card look like a status colour ("is green good?").
     * A gradient that starts white and only picks up its hue in the far corner
     * reads as paper catching light: the row still resolves left-to-right as
     * four different kinds of fact, but no card claims to be a verdict.
     *
     * Borders stay near-neutral. A coloured edge on top of a coloured wash was
     * what made the earlier flat version look heavy.
     */
    statGreen:  { bg: 'linear-gradient(135deg,#FFFFFF 42%,#EBF8F1 100%)', line: '#E5EBE9' },
    statBlue:   { bg: 'linear-gradient(135deg,#FFFFFF 42%,#EBF1FE 100%)', line: '#E2E7F1' },
    statViolet: { bg: 'linear-gradient(135deg,#FFFFFF 42%,#F1EDFD 100%)', line: '#E6E4EE' },
    statAmber:  { bg: 'linear-gradient(135deg,#FFFFFF 42%,#FDF2E8 100%)', line: '#EFE8E1' },

    /*
     * Evidence cards: a cool near-white, not grey.
     *
     * Grey read as disabled — a long column of quotes on flat grey looks like
     * content that has been switched off, and it dulled the green bar and the
     * why/when pills that carry the meaning. This keeps the column bright enough
     * to read at length while the faint blue cast ties it to the indigo rail,
     * and it sits clearly above the #F4F5FA canvas rather than merging with it.
     */
    doc:    { bg: '#FAFBFF', line: '#E3E7F2' },
    /*
     * Slate — the restrained pair.
     *
     * "Periods" counts quarters and "Company insights" opens a report; neither
     * is a finding, so neither earns a colour of its own. Giving them one shared
     * cool neutral keeps the coloured cards meaning something, and the two read
     * as the same kind of element in two different columns.
     */
    slate:  { bg: '#F4F6FA', line: '#DCE2EC' },
    indigo: { bg: '#F7F8FE', line: '#DDE2FB' },
    violet: { bg: '#FBF9FF', line: '#E7DFFB' },
    amber:  { bg: '#FFFCF5', line: '#F7E4C2' },
    green:  { bg: '#F7FDFA', line: '#CEEFDE' },
};

/**
 * The company mark in the hero.
 *
 * NOT `renderCompanyAvatar` — that returns a 28px tinted square, so putting it
 * on the white tile nested a small coloured box inside a bigger white one. Here
 * the tile IS the surface and the letter sits directly on it, which is what the
 * design draws.
 *
 * Colour is hashed from the name so a company keeps the same one everywhere,
 * and picked dark enough to hold its own against white.
 */
const MARK_COLORS = ['#B91C1C', '#B45309', '#047857', '#1D4ED8', '#6D28D9', '#BE185D', '#0F766E', '#3F57E0'];

const HeroMark = ({ name, logo }) => {
    const safeLogo = logo && !logo.includes('thenounproject') && !logo.includes('default') ? logo : null;
    const color = MARK_COLORS[
        (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % MARK_COLORS.length
    ];
    return (
        <div className="shrink-0 w-[60px] h-[60px] rounded-[18px] bg-white grid place-items-center overflow-hidden"
             style={{ boxShadow: '0 2px 8px rgba(15,23,42,.08)' }}>
            {safeLogo ? (
                <img src={safeLogo} alt={name} className="w-full h-full object-contain p-2"
                     onError={(e) => { e.target.style.display = 'none'; }} />
            ) : (
                <span className="text-[26px] font-extrabold leading-none" style={{ color }}>
                    {(name || 'C').charAt(0).toUpperCase()}
                </span>
            )}
        </div>
    );
};

/** A count with its unit and a one-line gloss — four of these sit under the header. */
const Stat = ({ icon, value, label, sub, accent, tone = 'plain' }) => {
    const t = TONES[tone] || TONES.plain;
    return (
    <div className="rounded-[14px] border" style={{ background: t.bg, borderColor: t.line }}>
        <div className="px-4 py-3.5 flex items-center gap-3">
            <div className="w-[34px] h-[34px] rounded-[10px] grid place-items-center shrink-0"
                 style={{ background: '#fff', color: accent, boxShadow: '0 1px 2px rgba(15,23,42,.06)' }}>
                {icon}
            </div>
            <div className="min-w-0">
                <div className="flex items-baseline gap-1.5">
                    <span className="text-[19px] font-extrabold leading-none tracking-tight"
                          style={{ color: value == null ? INK4 : INK }}>
                        {value == null ? '—' : value}
                    </span>
                    <span className="text-[11.5px] font-bold" style={{ color: MUT }}>{label}</span>
                </div>
                <div className="text-[11px] truncate mt-1" style={{ color: INK4 }} title={sub}>{sub}</div>
            </div>
        </div>
    </div>
    );
};

/** Rail container: a titled card with an optional action on the right. */
const RailCard = ({ title, icon, action, children, tone = 'plain', pad = '16px 18px' }) => {
    const t = TONES[tone] || TONES.plain;
    return (
    <div className="rounded-[14px] border overflow-hidden" style={{ background: t.bg, borderColor: t.line }}>
        <div className="flex items-center gap-2 px-[18px] py-[13px] border-b" style={{ borderColor: t.line }}>
            <span className="grid place-items-center" style={{ color: INK4 }}>{icon}</span>
            <div className="flex-1 text-[12.5px] font-bold tracking-tight" style={{ color: INK }}>{title}</div>
            {action}
        </div>
        <div style={{ padding: pad }}>{children}</div>
    </div>
    );
};

/**
 * A labelled fact under a quote — "Why", "When", "How many".
 *
 * A pill rather than a box: these are short phrases, and boxing each one in a
 * third of the row left most of that row empty while making three fragments
 * look like three paragraphs. Inline pills wrap to fit what they actually hold.
 */
const Pill = ({ icon, k, v, c, bg, line }) => (
    <span className="inline-flex items-center gap-[7px] rounded-full px-3 py-1.5"
          style={{ background: bg, border: `1px solid ${line}` }}>
        <span className="inline-flex items-center gap-1 text-[9.5px] font-extrabold uppercase tracking-[.07em] shrink-0"
              style={{ color: c }}>
            {icon}{k}
        </span>
        <span className="text-[12px]" style={{ color: INK7 }}>{v}</span>
    </span>
);

/**
 * Binds this page's title and actions to the shared navbar.
 *
 * Lives in its own component so the hooks can be skipped entirely when the page
 * is embedded — an inline expansion inside the board must not retitle the board
 * it is sitting in. Hooks cannot be called conditionally, but a component can be
 * rendered conditionally.
 */
const NavbarBinding = ({ companyName, saved, saving, onBack, onToggleSaved }) => {
    useNavbarTitle(
        {
            title: 'Hiring Signals (US)',
            subtitle: companyName ? `Company signal · ${companyName}` : 'Company signal',
        },
        [companyName]
    );
    useNavbarActions(
        <>
            <button
                onClick={onBack}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-[#334155] hover:bg-[#F1F3F7] transition-colors"
            >
                <ArrowLeft style={{ width: 14, height: 14 }} />
                All US signals
            </button>
            <button
                onClick={onToggleSaved}
                disabled={saving}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[12px] font-semibold border transition-colors ${
                    saved
                        ? 'bg-[#EEF1FE] text-[#3F57E0] border-[#C7D0F8] hover:bg-[#E0E5FC]'
                        : 'bg-white text-[#334155] border-[#E6E8EE] hover:border-[#D6DAE3]'
                } ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
                {saved ? <BookmarkCheck style={{ width: 13, height: 13 }} /> : <Bookmark style={{ width: 13, height: 13 }} />}
                {saved ? 'Saved' : 'Save'}
            </button>
        </>,
        [saved, saving, companyName]
    );
    return null;
};

/**
 * `embedded` renders just the detail content — no navbar binding, no page
 * chrome, no scroll container of its own — so the board can expand a company
 * inline the way the jobs list does. `tickerProp` supplies the company when
 * there is no /hiring-signals-us/:ticker route param to read.
 */
const PublicSignalDetailPage = ({ tickerProp = null, embedded = false }) => {
    const dispatch = useDispatch();
    const [insightsOpen, setInsightsOpen] = useState(false);
    // Referral path, mirroring the job page: pick how many contacts, pay per
    // contact actually found, then read them ranked.
    const [recruiterState, setRecruiterState] = useState('idle'); // idle | loading | done | empty
    const [recruiterData, setRecruiterData] = useState(null);
    const [contactCount, setContactCount] = useState(10);
    const [contactsError, setContactsError] = useState(null);
    // True until the free cache check answers. Without it the page renders the
    // "Find contacts" button for a moment on every load, inviting a reader to
    // pay again for contacts they already have.
    const [contactsPeeking, setContactsPeeking] = useState(true);
    const [contactsOpen, setContactsOpen] = useState(false);
    const [outreachOpen, setOutreachOpen] = useState(false);
    const [toast, setToast] = useState(null);
    // Evidence filter. Calls and filings say different kinds of thing — a
    // transcript gives a reason and a date, a 10-K gives an audited number — so
    // being able to read one without the other is worth a control.
    const [evidenceTab, setEvidenceTab] = useState('all');
    const [saved, setSaved] = useState(false);
    const [savedId, setSavedId] = useState(null);
    const [saving, setSaving] = useState(false);
    // Positioning advice. Behind a click because generating it costs an LLM
    // call — everything else in this card is free arithmetic over stored data.
    const [positioning, setPositioning] = useState(null);
    const [positioningState, setPositioningState] = useState('idle'); // idle | loading | done | empty | error
    const user = useSelector((st) => st.auth.user);
    const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };
    const { ticker: routeTicker } = useParams();
    const ticker = tickerProp || routeTicker;
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const { data: res } = await api.get(`/api/publicsignals/company/${encodeURIComponent(ticker)}`);
                if (!cancelled) {
                    if (res?.success) setData(res);
                    else setError(res?.message || 'Could not load this company.');
                }
            } catch (err) {
                if (!cancelled) setError(err?.response?.data?.message || 'Could not load this company.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [ticker]);

    /*
     * Anything already paid for is restored on load — contacts and positioning
     * both. Both reads are peek-only and free; the point is that a button which
     * spends money must never reappear once its work is done, whether the reader
     * refreshed, navigated away, or came back tomorrow.
     */
    useEffect(() => {
        if (!data?.company?.companyName) return;
        const name = data.company.companyName;
        let cancelled = false;

        (async () => {
            setContactsPeeking(true);
            try {
                const cached = await dispatch(peekRecruiterForJob({ jobDetails: { companyName: name } })).unwrap();
                const found = [
                    ...(cached?.hiring_manager?.best_guesses || []),
                    ...(cached?.recruiters?.best_guesses || [])
                ];
                if (!cancelled && found.length > 0) {
                    setRecruiterData(cached);
                    setRecruiterState('done');
                }
            } catch { /* no cached contacts — the CTA is correct */ }
            finally { if (!cancelled) setContactsPeeking(false); }
        })();

        (async () => {
            try {
                const res = await dispatch(peekCompanyPositioning({ companyName: name })).unwrap();
                if (!cancelled && res?.bullets?.length) {
                    setPositioning(res.bullets);
                    setPositioningState('done');
                }
            } catch { /* not generated yet — the button is correct */ }
        })();

        return () => { cancelled = true; };
    }, [dispatch, data?.company?.companyName]);

    // Saved state is its own request so a slow bookmark lookup never delays the
    // signals, and a failure here just leaves the button in its unsaved state.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data: res } = await api.get(`/api/publicsignals/saved/${encodeURIComponent(ticker)}`);
                if (!cancelled && res?.success) { setSaved(Boolean(res.saved)); setSavedId(res.savedId || null); }
            } catch { /* leave unsaved */ }
        })();
        return () => { cancelled = true; };
    }, [ticker]);

    // Newest first, and within a document the signals that carry the most detail
    // lead — a quote with a headcount and a reason is worth more to a reader than
    // a bare sentence.
    const documents = useMemo(() => {
        if (!data?.documents) return [];
        return data.documents.map((doc) => ({
            ...doc,
            /*
             * HIRING EVIDENCE ONLY, on the user-facing page.
             *
             * A company can be growing one team while cutting another, and the
             * board this page is reached from now shows hiring alone. Listing
             * its cutting quotes here would reintroduce through the back door
             * exactly what the board stopped showing — and a job seeker reading
             * "we eliminated 300 roles" under a company they were about to
             * apply to is being shown the outreach dataset, not a hiring signal.
             *
             * The admin panel renders this same page against the same endpoint
             * and keeps every direction.
             */
            signals: [...(doc.signals || [])].filter((sig) => sig.direction !== 'negative').sort((a, b) => {
                const detail = (s) => (s.magnitude ? 4 : 0) + (s.rationale ? 2 : 0) + (s.timeframe ? 1 : 0) + (s.department ? 1 : 0);
                return detail(b) - detail(a);
            }),
        })).filter((doc) => doc.signals.length > 0);
    }, [data]);

    const shell = (children) => (embedded ? (
        // Plain flow: the board owns the scrolling and the chrome.
        <div className="text-left" style={{ background: CANVAS }}>{children}</div>
    ) : (
        <div className="relative h-screen pt-16 flex flex-col overflow-hidden text-left" style={{ background: CANVAS }}>
            <Navbaar />
            <div className="flex flex-1 min-h-0 transition-all duration-300">
                <div className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0">
                    <Sidebar />
                </div>
                <div className="flex-1 overflow-y-auto hide-scrollbar">{children}</div>
            </div>
        </div>
    ));

    const toggleSaved = async () => {
        if (saving) return;
        setSaving(true);
        // Optimistic: the button is the only feedback, so it should move now and
        // roll back if the request fails, rather than sit still for a round trip.
        const wasSaved = saved;
        setSaved(!wasSaved);
        try {
            if (wasSaved) {
                if (savedId) await api.delete(`/api/savedcompanies/deletesavedcompany/${savedId}`);
                setSavedId(null);
                showToast('Removed from Saved Jobs & Signals');
            } else {
                const { data: res } = await api.post(`/api/publicsignals/save/${encodeURIComponent(ticker)}`);
                if (!res?.success) throw new Error(res?.message || 'save failed');
                setSavedId(res.savedId || null);
                showToast('Saved — find it under Saved Jobs & Signals');
            }
        } catch (err) {
            setSaved(wasSaved);
            showToast(err?.response?.data?.message || 'Could not update your saved signals');
        } finally {
            setSaving(false);
        }
    };

    /*
     * Embedded loading gets a FIXED height, not the full-page skeleton.
     *
     * That skeleton is sized for a whole viewport, so inside a table row it
     * opened a screen-and-a-half of empty grey and shoved the rest of the board
     * out of sight — then collapsed back when the data landed. A fixed box keeps
     * the row's growth honest and the jump small.
     */
    if (loading) return shell(embedded ? <EmbeddedLoader ticker={ticker} /> : <DetailSkeleton />);

    if (error || !data) {
        return shell(
            <div className="px-6 py-20 text-center">
                <Building className="w-8 h-8 mx-auto text-gray-300 mb-3" />
                <p className="text-[14px] text-gray-600">{error || 'Company not found.'}</p>
                <button onClick={() => navigate(-1)} className="mt-4 text-[13px] font-semibold text-[#3F57E0] hover:underline">
                    Back to Hiring Signals (US)
                </button>
            </div>
        );
    }

    const { company, summary, related, fit } = data;

    /*
     * Contacts come back split into hiring managers and recruiters, and the two
     * lists overlap — the same person can appear in both. Dedupe on profile URL
     * before ranking, or a duplicate row reads as two different people.
     */
    const allContacts = (() => {
        if (!recruiterData) return [];
        const seen = new Set();
        return [
            ...(recruiterData.hiring_manager?.best_guesses || []),
            ...(recruiterData.recruiters?.best_guesses || [])
        ].filter((c) => {
            const k = c.profile_url || c.linkedin_url || c.name;
            if (!k || seen.has(k)) return false;
            seen.add(k);
            return true;
        }).sort((a, b) =>
            ((b.relevance?.score ?? -1) - (a.relevance?.score ?? -1))
            || ((b.confidence_score || 0) - (a.confidence_score || 0))
        );
    })();

    const handleFindContacts = async () => {
        setRecruiterState('loading');
        setContactsError(null);
        try {
            /*
             * No job id and no organizationId — a company page has neither.
             * The endpoint only requires a company name (it throws
             * NO_COMPANY_NAME otherwise) and already has a lookup path that
             * matches contacts on organization_name, so the name is enough.
             *
             * Passing the ticker as `_id` would be worse than passing nothing:
             * it is not a valid ObjectId, so the cache lookup would log a
             * warning and skip anyway.
             */
            const result = await dispatch(fetchRecruiterForJob({
                jobDetails: {
                    companyName: company.companyName,
                    companyWebsite: company.domain || company.website || null,
                    jobLocation: company.hqLocation || null
                },
                numberOfContacts: contactCount
            })).unwrap();

            const found = [
                ...(result?.hiring_manager?.best_guesses || []),
                ...(result?.recruiters?.best_guesses || [])
            ];
            if (found.length === 0) { setRecruiterState('empty'); return; }
            setRecruiterData(result);
            setRecruiterState('done');
        } catch (err) {
            // Out of credits is not "nobody works here" — say so and let them
            // retry, rather than showing an empty result they cannot act on.
            if (err?.error === 'QuotaExceeded') {
                setContactsError(err?.message || 'You are out of credits — buy more to fetch contacts.');
                setRecruiterState('idle');
            } else {
                setRecruiterState('empty');
            }
        }
    };

    /*
     * Activating turns the strategy into a dated plan on the Daily Tasks board.
     *
     * The sprint is keyed by (sourceType, sourceId) and `sourceId` is a plain
     * string on the model, so the ticker is a legitimate key — there is no
     * HSignal document behind a public company to point at. `pathId` stays
     * 'signal' because that is what this is: a company likely to hire, with no
     * posted role yet.
     */
    const loadPositioning = async () => {
        if (positioningState === 'loading') return;
        setPositioningState('loading');
        try {
            /*
             * Same endpoint the Company Insights modal uses, and the same cache:
             * positioning is stored per (user, company), so opening this twice
             * is free, and a reader who already opened Insights pays nothing.
             */
            const res = await dispatch(fetchCompanyInsights({
                companyName: company.companyName,
                ticker: company.ticker || ticker,
                includePositioning: true
            })).unwrap();
            const bullets = res?.insights?.howToPositionYourself || [];
            if (bullets.length === 0) {
                setPositioningState(res?.positioningStatus === 'no_profile' ? 'empty' : 'error');
                return;
            }
            setPositioning(bullets);
            setPositioningState('done');
        } catch {
            setPositioningState('error');
        }
    };

    const handleActivateStrategy = async (pathId, strategy, contacts = []) => {
        if (!user?._id) { showToast('Please sign in to activate a strategy'); return false; }
        if (pathId !== 'signal') { showToast("This strategy can't be activated here"); return false; }
        try {
            await dispatch(activateSprint({
                userId: user._id,
                sourceType: 'signal',
                sourceId: company.ticker,
                pathId,
                subStrategyKey: strategy?.key || null,
                strategyName: strategy?.name || null,
                pathTitle: 'Warm a Hiring Signal',
                plan: Array.isArray(strategy?.days)
                    ? strategy.days.map((d, i) => ({
                        label: d.label || `Week ${i + 1}`,
                        action: d.action || '',
                        channel: d.channel || null,
                        goal: d.goal || null
                    }))
                    : [],
                contacts: (Array.isArray(contacts) ? contacts : []).slice(0, 8).map((c) => ({
                    contactid: c.contactid || c._id || c.profile_url || c.linkedin_url || c.name,
                    name: c.name || 'Unknown',
                    title: c.title || '',
                    email: c.email || null,
                    profile_url: c.profile_url || c.linkedin_url || null,
                    profile_photo: c.profile_photo || c.photo_url || null,
                    predicted_role: c.predicted_role || c._fullData?.lead_category || null,
                    industry: c.industry || null
                })),
                companyName: company.companyName,
                companyWebsite: company.website || null,
                companyLogo: null,
                organizationId: null,
                jobTitle: null
            })).unwrap();
            showToast('Strategy activated — added to your Daily Tasks');
            setOutreachOpen(false);
            navigate('/dailytasks');
            return true;
        } catch (e) {
            showToast(typeof e === 'string' ? e : 'Could not activate strategy');
            return false;
        }
    };

    /*
     * Counted from what is actually rendered.
     *
     * `summary.hiringSignals` comes from the API and counts every positive
     * signal the company has; the list below is filtered and its documents can
     * drop out entirely. Showing the API's number over a shorter list is the
     * kind of quiet mismatch that makes a page look broken.
     */
    /*
     * Calls and filings answer different questions — a transcript gives a reason
     * and a date, a 10-K gives an audited number — so a reader working out
     * WHEN to reach out can drop the filings, and one checking the headcount can
     * drop the calls. Filtering never changes the counts above it: those describe
     * the company, not the current view.
     */
    const shownDocuments = documents.filter((d) =>
        evidenceTab === 'all'
        || (evidenceTab === 'calls' ? d.docType === 'transcript' : d.docType !== 'transcript')
    );

    const shownSignals = documents.reduce((n, d) => n + d.signals.length, 0);

    return shell(
        <div className="px-6 pt-6 pb-20" style={{ color: INK }}>
            {!embedded && (
                <NavbarBinding
                    companyName={company.companyName}
                    saved={saved}
                    saving={saving}
                    onBack={() => navigate(-1)}
                    onToggleSaved={toggleSaved}
                />
            )}
            {/*
                HERO — identity and the three scores, on one gradient panel.

                The scores are a summary of everything below, so they sit beside
                the company rather than after it. Rings rather than bars: three
                of them read at a glance, and this is the one block on the page
                worth the height.
            */}
            <div className="rounded-[22px] px-7 py-[26px] mb-[18px]"
                 style={{ background: 'linear-gradient(135deg,#EEF1FE 0%,#F7F5FF 45%,#F2FBF6 100%)', border: '1px solid #DCE1FA' }}>
                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-7 items-center">
                    <div className="flex gap-[18px] items-start min-w-0">
                        <HeroMark name={company.companyName} />

                        <div className="min-w-0">
                            <h1 className="m-0 text-[26px] font-extrabold tracking-[-0.03em] leading-tight" style={{ color: INK }}>
                                {company.companyName}
                            </h1>

                            <div className="flex flex-wrap items-center gap-2 mt-2.5">
                                <span className="text-[11px] font-extrabold tracking-[.04em] bg-white px-2.5 py-[5px] rounded-[7px]"
                                      style={{ color: '#3346B8' }}>
                                    {company.ticker}{company.exchange ? ` · ${company.exchange}` : ''}
                                </span>
                                {company.industry && (
                                    <span className="text-[11.5px] font-bold px-2.5 py-[5px] rounded-full"
                                          style={{ color: '#6D28D9', background: '#EDE4FE' }}>
                                        {company.industry}
                                    </span>
                                )}
                                {/* No HQ chip: we hold no location for public companies,
                                    and inventing one is worse than leaving it out. */}
                                {company.sicDescription && (
                                    <span className="text-[11.5px] font-bold bg-white px-2.5 py-[5px] rounded-full"
                                          style={{ color: INK7 }} title="SEC industry classification">
                                        {company.sicDescription}
                                    </span>
                                )}
                            </div>

                            {summary.latestHeadcount && (() => {
                                /*
                                 * Coloured by ITS OWN number, not the company's net direction.
                                 * GitLab's headcount grew 8.6% while it made more cutting
                                 * statements than hiring ones — a figure carrying a sign has
                                 * to be coloured by that sign.
                                 */
                                const raw = summary.latestHeadcount || '';
                                const pct = /([+-]?\d+(?:\.\d+)?)\s*%/.exec(raw);
                                const delta = pct ? Number(pct[1]) : null;
                                const from = /from\s+([\d,]*\d)/i.exec(raw);
                                const headcount = raw.split('(')[0].replace(/employees?/i, '').trim();
                                const span = formatSpan(summary.latestHeadcountPriorEnd, summary.latestHeadcountPeriodEnd);
                                const chip = delta === null
                                    ? { bg: '#F1F5F9', fg: '#475569' }
                                    : delta < 0 ? { bg: '#FEE2E2', fg: '#B91C1C' } : { bg: '#DCFCE7', fg: '#047857' };

                                return (
                                    <div className="flex items-center gap-2.5 mt-4 flex-wrap">
                                        <span className="inline-flex items-center gap-2 bg-white rounded-full px-3.5 py-2"
                                              style={{ boxShadow: '0 1px 3px rgba(15,23,42,.06)' }}>
                                            <Users className="w-3.5 h-3.5" style={{ color: '#3F57E0' }} />
                                            <b className="text-[15px] tracking-[-0.02em]" style={{ color: INK }}>{headcount}</b>
                                            <span className="text-[12px]" style={{ color: MUT }}>employees</span>
                                            {delta !== null && (
                                                <span className="text-[11px] font-extrabold px-2 py-[3px] rounded-full"
                                                      style={{ background: chip.bg, color: chip.fg }}>
                                                    {delta < 0 ? '▼' : '▲'} {Math.abs(delta)}%
                                                </span>
                                            )}
                                        </span>
                                        {/* A percentage is not interpretable without the window
                                            it was measured over. */}
                                        <span className="text-[11.5px]" style={{ color: MUT }}>
                                            {from ? `up from ${from[1]}` : 'latest headcount'}
                                            {span ? ` · ${span.from} → ${span.to}` : ''}
                                        </span>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>

                    {/* No panel behind the dials — the hero gradient carries them,
                        and a white sheet on top of it just muddied the wash. */}
                    <div className="flex justify-between gap-2 px-3.5 py-[18px]">
                        <Dial score={fit?.hiringScore} label="Hiring signal" hint={signalHint(fit?.hiringScore)} c="#D97706" bg="#FDE9C8" />
                        <Dial score={fit?.fitScore} label="Your fit" hint={fitHint(fit?.fitScore)} c="#3F57E0" bg="#D8DEFB" />
                        <Dial score={fit?.overallScore} label="Overall" hint={overallHint(fit?.overallScore)} c="#047857" bg="#C4EEDA" />
                    </div>
                </div>
            </div>

            {/* STATS — four counts, each with the unit spelled out. */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                <Stat icon={<TrendingUp className="w-4 h-4" />} value={shownSignals} label="signals"
                      sub="Statements about growing" accent="#047857" tone="statGreen" />
                <Stat icon={<Briefcase className="w-4 h-4" />} value={documents.length} label="documents"
                      sub={(summary.sources || []).join(' · ') || 'sources'} accent="#1D4ED8" tone="statBlue" />
                <Stat icon={<Calendar className="w-4 h-4" />} value={summary.periods} label="periods"
                      sub="Quarters & years covered" accent="#6D28D9" tone="statViolet" />
                <Stat icon={<Users className="w-4 h-4" />} value={allContacts.length || null} label="contacts"
                      sub={allContacts.length ? 'Verified referral paths' : 'Not fetched yet'} accent="#D97706" tone="statAmber" />
            </div>

            {/*
                Evidence leads, everything else supports. The filings are the
                reason this page exists, so they take the main column; fit,
                contacts and related companies are what a reader does NEXT and
                ride along in a sticky rail.
            */}
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_372px] gap-5 items-start">
                <div className="min-w-0">
                    <div className="flex items-end justify-between gap-4 mb-3 flex-wrap">
                        <div>
                            <h2 className="text-[17px] font-extrabold tracking-tight" style={{ color: INK }}>Evidence</h2>
                            <p className="text-[11.5px] mt-1" style={{ color: MUT }}>
                                Every claim links back to a filing or transcript. Newest first.
                            </p>
                        </div>
                        <div className="flex gap-1.5 p-[3px] rounded-[9px]" style={{ background: '#F1F3F7' }}>
                            {[
                                ['all', 'All', documents.length],
                                ['filings', 'Filings', documents.filter((d) => d.docType !== 'transcript').length],
                                ['calls', 'Calls', documents.filter((d) => d.docType === 'transcript').length],
                            ].map(([key, label, n]) => (
                                <button key={key} onClick={() => setEvidenceTab(key)} disabled={n === 0}
                                        className="text-[11.5px] font-bold px-3 py-[5px] rounded-[7px] transition-colors disabled:opacity-40"
                                        style={evidenceTab === key
                                            ? { background: '#fff', color: INK, boxShadow: '0 1px 2px rgba(15,23,42,.08)' }
                                            : { color: MUT }}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-col gap-3.5">
                        {shownDocuments.map((doc, di) => {
                            const meta = SOURCE_META[doc.docType] || { label: doc.docType, short: doc.docType, icon: FileText, why: '' };
                            const isCall = doc.docType === 'transcript';
                            const k = isCall ? { c: '#6D28D9', bg: '#F5F3FF' } : { c: '#1D4ED8', bg: '#EFF6FF' };
                            const KIcon = isCall ? Mic : Building;
                            return (
                                <div key={`${doc.docType}-${doc.periodKey}-${di}`}
                                     className="rounded-[14px] border overflow-hidden"
                                     style={{ background: TONES.doc.bg, borderColor: TONES.doc.line }}>
                                    <div className="px-5 py-4 flex items-start gap-3.5 flex-wrap">
                                        <span className="inline-flex items-center gap-1.5 shrink-0 text-[11px] font-extrabold px-2.5 py-1.5 rounded-lg"
                                              style={{ color: k.c, background: k.bg }}>
                                            <KIcon className="w-3 h-3" />{meta.short}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[14px] font-bold tracking-tight" style={{ color: INK }}>{meta.label}</div>
                                            {/* What this source is worth, in plain terms — a reader
                                                should not need to know what a 10-K is to weigh it. */}
                                            <div className="text-[11.5px] mt-0.5" style={{ color: MUT }}>{meta.why}</div>
                                        </div>
                                        <div className="flex items-center gap-3.5 shrink-0">
                                            <span className="text-[11.5px] font-semibold" style={{ color: INK7 }}>{doc.periodKey}</span>
                                            {doc.documentDate && (
                                                <span className="text-[11.5px]" style={{ color: INK4 }}>{getDaysAgo(doc.documentDate)}</span>
                                            )}
                                            {doc.sourceUrl && (
                                                <a href={doc.sourceUrl} target="_blank" rel="noopener noreferrer"
                                                   className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#3F57E0] hover:underline">
                                                    Filing <ExternalLink className="w-3 h-3" />
                                                </a>
                                            )}
                                        </div>
                                    </div>

                                    {doc.signals.length === 0 && (
                                        <p className="px-5 py-4 text-[13px] italic border-t" style={{ color: INK4, borderColor: TONES.doc.line }}>
                                            Read, but nothing was said about people in this document.
                                        </p>
                                    )}

                                    {doc.signals.map((sig, si) => {
                                        const dm = DIRECTION_META[sig.direction] || DIRECTION_META.neutral;
                                        const t = sig.direction === 'positive'
                                            ? { c: '#047857', bg: '#ECFDF5', bar: '#34D399' }
                                            : sig.direction === 'negative'
                                                ? { c: '#B91C1C', bg: '#FEF2F2', bar: '#F87171' }
                                                : { c: '#475569', bg: '#F1F5F9', bar: '#CBD5E1' };
                                        const span = formatSpan(sig.priorPeriodEnd, sig.periodEnd);
                                        return (
                                            <div key={si} className="px-5 pt-4 pb-[18px] border-t" style={{ borderColor: TONES.doc.line }}>
                                                <div className="flex flex-wrap items-center gap-2 mb-2.5">
                                                    <span className="text-[9.5px] font-extrabold tracking-[.07em] px-2 py-1 rounded-md"
                                                          style={{ color: t.c, background: t.bg }}>
                                                        {dm.label.toUpperCase()}
                                                    </span>
                                                    {sig.category && (
                                                        <span className="text-[10.5px] font-semibold px-2 py-[3px] rounded-full"
                                                              style={{ background: '#EEF1F8', color: INK7 }}>
                                                            {String(sig.category).replace(/_/g, ' ')}
                                                        </span>
                                                    )}
                                                    {sig.department && (
                                                        <span className="text-[10.5px] font-semibold px-2 py-[3px] rounded-full"
                                                              style={{ background: '#EEF1F8', color: INK7 }}>
                                                            {sig.department}
                                                        </span>
                                                    )}
                                                    {/*
                                                        The skills the company named, in its own words.
                                                        Only 143 companies have any — but where it exists
                                                        it is the most specific thing on the page.
                                                    */}
                                                    {(sig.skills || []).map((sk) => (
                                                        <span key={sk} className="text-[10.5px] font-semibold px-2 py-[3px] rounded-full ring-1 ring-inset ring-cyan-200"
                                                              style={{ background: '#ECFEFF', color: '#155E75' }}>
                                                            {sk}
                                                        </span>
                                                    ))}
                                                    {sig.speaker && (
                                                        <span className="text-[11.5px] ml-auto" style={{ color: MUT }}>
                                                            — {sig.speaker}{sig.role ? `, ${sig.role}` : ''}
                                                        </span>
                                                    )}
                                                </div>

                                                {sig.quote && (
                                                    <div className="pl-3.5" style={{ borderLeft: `3px solid ${t.bar}` }}>
                                                        <p className="text-[13.5px] leading-[1.62] m-0" style={{ color: INK7, textWrap: 'pretty' }}>
                                                            &ldquo;{sig.quote}&rdquo;
                                                        </p>
                                                    </div>
                                                )}

                                                {(sig.magnitude || sig.rationale || sig.timeframe) && (
                                                    <div className="flex flex-wrap gap-2 mt-3 pl-[17px]">
                                                        {sig.magnitude && (
                                                            <Pill icon={<Users className="w-[11px] h-[11px]" />} k="How many"
                                                                  v={span ? `${sig.magnitude} · ${span.from} → ${span.to}` : sig.magnitude}
                                                                  c="#3346B8" bg="#F5F6FE" line="#D6DCFB" />
                                                        )}
                                                        {sig.rationale && (
                                                            <Pill icon={<HelpCircle className="w-[11px] h-[11px]" />} k="Why"
                                                                  v={sig.rationale} c="#047857" bg="#F2FBF6" line="#C8EDDA" />
                                                        )}
                                                        {sig.timeframe && (
                                                            <Pill icon={<Clock className="w-[11px] h-[11px]" />} k="When"
                                                                  v={sig.timeframe} c="#B91C1C" bg="#FEF2F2" line="#F8D4D4" />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* RAIL */}
                <aside className="flex flex-col gap-3.5 xl:sticky xl:top-4">
                    {/* Company Insights — cached per company, so opening it twice is free. */}
                    <button onClick={() => setInsightsOpen(true)}
                            className="w-full rounded-[14px] px-[18px] py-3.5 flex items-center gap-3 text-left transition-colors hover:brightness-[.985]"
                            style={{ background: TONES.slate.bg, border: `1px solid ${TONES.slate.line}` }}>
                        {/* White chip, glyph in slate — the same treatment the stat
                            row uses, so the two line up across columns. */}
                        <span className="w-[34px] h-[34px] rounded-[10px] grid place-items-center shrink-0"
                              style={{ background: '#fff', color: '#475569', boxShadow: '0 1px 2px rgba(15,23,42,.06)' }}>
                            <BarChart2 className="w-4 h-4" />
                        </span>
                        <span className="flex-1 min-w-0">
                            <span className="block text-[12.5px] font-bold" style={{ color: INK }}>Company insights</span>
                            <span className="block text-[11px] mt-0.5" style={{ color: MUT }}>Culture, growth &amp; toxicity</span>
                        </span>
                        <ChevronRight className="w-[15px] h-[15px] shrink-0" style={{ color: INK4 }} />
                    </button>

                    {/*
                        REFERRAL PATH — same flow as the job page: choose a count,
                        pay only for contacts actually found. Identical wording
                        matters; a user who has seen it on a job should not have to
                        work out whether this one charges differently.
                    */}
                    <RailCard
                        tone="violet"
                        title="Referral path"
                        icon={<Users className="w-3.5 h-3.5" style={{ color: '#3F57E0' }} />}
                        action={recruiterState === 'done'
                            ? <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-full" style={{ background: '#EEF1FE', color: '#3F57E0' }}>
                                {allContacts.length} contacts
                              </span>
                            : null}
                        pad="6px 18px 16px"
                    >
                        {recruiterState === 'idle' && contactsPeeking && (
                            <div className="flex items-center justify-center gap-2 py-6 text-[12.5px]" style={{ color: INK4 }}>
                                <RefreshCw className="w-4 h-4 animate-spin" /> Loading contacts…
                            </div>
                        )}

                        {recruiterState === 'idle' && !contactsPeeking && (
                            <div className="flex flex-col gap-3 pt-2.5">
                                <p className="text-[11.5px] leading-[1.5]" style={{ color: MUT }}>
                                    Find verified contacts at{' '}
                                    <span className="font-bold" style={{ color: INK }}>{company.companyName}</span> who can refer you.
                                </p>
                                <div className="flex flex-col gap-1.5">
                                    <span className="text-[12px] font-medium" style={{ color: INK7 }}>Contacts to fetch</span>
                                    <div className="grid grid-cols-5 gap-1">
                                        {[10, 20, 30, 40, 50].map((n) => (
                                            <button key={n} onClick={() => setContactCount(n)}
                                                    className={`py-1 rounded-lg text-[12.5px] font-semibold border transition-colors ${
                                                        contactCount === n
                                                            ? 'bg-[#3F57E0] border-[#3F57E0] text-white'
                                                            : 'bg-white border-[#E6E8EE] text-[#475569] hover:border-[#3F57E0] hover:text-[#3F57E0]'
                                                    }`}>
                                                {n}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <button onClick={handleFindContacts}
                                        className="w-full py-2 rounded-xl bg-[#3F57E0] hover:bg-[#3347c8] text-white text-[13px] font-semibold transition-colors flex items-center justify-center gap-2">
                                    <Users className="w-4 h-4" /> Find contacts · up to {contactCount} credit{contactCount !== 1 ? 's' : ''}
                                </button>
                                <p className="text-[11px] text-center -mt-1" style={{ color: INK4 }}>
                                    1 credit per contact found — you only pay for what we find.
                                </p>
                                {contactsError && <p className="text-[11.5px] text-amber-600 text-center">{contactsError}</p>}
                            </div>
                        )}

                        {recruiterState === 'loading' && (
                            <div className="flex items-center justify-center gap-2 py-6 text-[12.5px]" style={{ color: INK4 }}>
                                <RefreshCw className="w-4 h-4 animate-spin" /> Searching contacts…
                            </div>
                        )}

                        {recruiterState === 'empty' && (
                            <p className="text-[12.5px] text-center py-4" style={{ color: INK4 }}>No verified contacts found.</p>
                        )}

                        {recruiterState === 'done' && (
                            <>
                                <p className="text-[11.5px] leading-[1.5] pt-2.5 pb-1" style={{ color: MUT }}>
                                    Ranked against your resume — seniority, tenure, and shared background.
                                    Recruiters and hiring managers first.
                                </p>
                                {allContacts.slice(0, 5).map((c, i) => (
                                    <ContactCard key={i} contact={c} />
                                ))}
                                <div className="flex gap-2 mt-3.5">
                                    {/*
                                        Always shown, never only when the list overflows.
                                        The modal is the only place carrying the ranking,
                                        the tier, the caveats and how to approach each
                                        person — hiding its door at five contacts or fewer
                                        made all of that unreachable.
                                    */}
                                    <button onClick={() => setContactsOpen(true)}
                                            className="flex-1 py-2 rounded-xl bg-[#EEF1FE] hover:bg-[#E0E6FC] text-[#3F57E0] text-[12.5px] font-semibold transition-colors">
                                        {allContacts.length > 5 ? `View all ${allContacts.length}` : 'Contact details'}
                                    </button>
                                    <button onClick={() => setOutreachOpen(true)}
                                            className="py-2 rounded-xl bg-[#3F57E0] hover:bg-[#3347c8] text-white text-[12.5px] font-semibold transition-colors flex items-center justify-center gap-1.5"
                                            style={{ flex: 1.4 }}>
                                        Outreach strategy
                                    </button>
                                </div>
                            </>
                        )}
                    </RailCard>

                    {/*
                        YOUR FIT — computed from stored filings against this
                        reader's profile, not from a model asked to rate the
                        match. That is why each line can quote its document.
                    */}
                    {fit && (
                        <RailCard
                            tone="indigo"
                            title={`Your fit · ${fit.fitScore != null ? `${fit.fitScore}/10` : 'not scored'}`}
                            icon={<Target className="w-3.5 h-3.5" style={{ color: '#3F57E0' }} />}
                            action={<span className="text-[10.5px]" style={{ color: INK4 }}>
                                {fit.factorsUsed} of {fit.factorsPossible} attributes
                            </span>}
                        >
                            {(fit.reasons.length > 0 || (fit.gaps || []).length > 0) ? (
                                <div className="flex flex-col gap-2.5">
                                    {fit.reasons.map((r, i) => (
                                        <div key={`m-${i}`} className="flex gap-2.5 items-start">
                                            <span className="w-4 h-4 rounded-full shrink-0 mt-[3px] grid place-items-center"
                                                  style={{ background: '#ECFDF5', color: '#047857' }}>
                                                <Check className="w-2.5 h-2.5" />
                                            </span>
                                            <div className="min-w-0">
                                                <p className="text-[12.5px] leading-[1.5]" style={{ color: INK7 }}>{r.text}</p>
                                                {/* The filing behind the claim. Without it this is
                                                    flattery; with it the reader can check. */}
                                                {r.quote && (
                                                    <p className="text-[10.5px] italic leading-snug mt-1" style={{ color: INK4 }}>
                                                        &ldquo;{r.quote.length > 120 ? `${r.quote.slice(0, 120)}…` : r.quote}&rdquo;
                                                        {r.docType && <span className="not-italic font-semibold"> · {r.docType}</span>}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    {(fit.gaps || []).map((g, i) => (
                                        <div key={`g-${i}`} className="flex gap-2.5 items-start">
                                            <span className="w-4 h-4 rounded-full shrink-0 mt-[3px] grid place-items-center text-[11px] font-extrabold leading-none"
                                                  style={{ background: '#FEF2F2', color: '#B91C1C' }}>–</span>
                                            <p className="text-[12.5px] leading-[1.5]" style={{ color: INK7 }}>{g}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-[12.5px] leading-[1.5]" style={{ color: MUT }}>
                                    Their filings name too few comparable attributes to score a fit.
                                </p>
                            )}

                            {/*
                                Positioning is the one paid thing on this page, so
                                it is a button rather than something that happens on
                                load — and it sits under the evidence it argues from.
                            */}
                            {/* Same 135deg wash as the stat cards, so the one block of
                                advice on this page still belongs to the same surface family
                                as the counts and the evidence. */}
                            <div className={`mt-3.5 rounded-xl overflow-hidden ${positioningState === 'idle' ? '' : 'px-3.5 py-3'}`}
                                 style={{
                                     // While idle the button fills this box edge to edge, so the
                                     // box contributes only its border — a second gradient behind
                                     // the button's own would just muddy it.
                                     background: positioningState === 'idle' ? 'transparent' : 'linear-gradient(135deg,#FDFCFF 38%,#EFE9FE 100%)',
                                     border: '1px solid #E4DDFB'
                                 }}>
                                {/* The heading is the button's job while nothing has been
                                    generated — a label above a button that says the same
                                    thing is the same words twice. */}
                                {positioningState !== 'idle' && (
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <Sparkles className="w-3 h-3" style={{ color: '#6D28D9' }} />
                                        <span className="text-[10.5px] font-extrabold uppercase tracking-[.07em]" style={{ color: '#6D28D9' }}>
                                            How to position yourself
                                        </span>
                                    </div>
                                )}

                                {positioningState === 'idle' && (
                                    <button onClick={loadPositioning}
                                            className="w-full h-full inline-flex items-center justify-center gap-2 px-4 py-4 text-[12.5px] font-bold transition-all hover:brightness-[.97]"
                                            style={{
                                                background: 'linear-gradient(135deg,#F3EEFE 0%,#E7DCFD 55%,#DCCEFB 100%)',
                                                color: '#5B21B6'
                                            }}>
                                        <Sparkles className="w-4 h-4" />
                                        How to position yourself
                                    </button>
                                )}
                                {positioningState === 'loading' && (
                                    <div className="flex items-center justify-center gap-2 py-2 text-[12px]" style={{ color: INK4 }}>
                                        <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Working out your angle…
                                    </div>
                                )}
                                {positioningState === 'empty' && (
                                    <p className="text-[12px] leading-[1.5]" style={{ color: MUT }}>
                                        Add your skills and experience in{' '}
                                        <button onClick={() => navigate('/profile')} className="font-bold text-[#3F57E0] hover:underline">Profile</button>
                                        {' '}to get advice matched to your background.
                                    </p>
                                )}
                                {positioningState === 'error' && (
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-[12px]" style={{ color: MUT }}>Couldn&rsquo;t generate advice.</span>
                                        <button onClick={loadPositioning} className="text-[12px] font-bold text-[#3F57E0] hover:underline">Try again</button>
                                    </div>
                                )}
                                {positioningState === 'done' && (
                                    <>
                                        <ul className="m-0 pl-4 flex flex-col gap-[7px]">
                                            {positioning.map((b, i) => (
                                                <li key={i} className="text-[12px] leading-[1.5]" style={{ color: INK7 }}>{b}</li>
                                            ))}
                                        </ul>
                                        {/* Named, not hidden: this part is a model's advice, while
                                            everything above it is quoted from filings. */}
                                        <div className="text-[10.5px] mt-2.5" style={{ color: INK4 }}>
                                            AI-generated from your profile and public company research.
                                        </div>
                                    </>
                                )}
                            </div>
                        </RailCard>
                    )}

                    {/*
                        RELATED SIGNALS — same industry, because SIC is the one
                        relation this data really supports. A similarity score
                        built from headcount would be a guess presented as a
                        recommendation.
                    */}
                    {(related || []).length > 0 && (
                        <RailCard tone="plain" title="Related signals"
                                  icon={<TrendingUp className="w-3.5 h-3.5" style={{ color: '#3F57E0' }} />}
                                  pad="6px 18px 12px">
                            {related.map((r, i) => (
                                <button key={r.ticker} onClick={() => navigate(`/hiring-signals-us/${r.ticker}`)}
                                        className="w-full flex items-center gap-2.5 py-2.5 text-left hover:opacity-80 transition-opacity"
                                        style={{ borderBottom: i < related.length - 1 ? `1px solid ${DIV}` : 'none' }}>
                                    <div className="shrink-0">{renderCompanyAvatar(null, r.companyName)}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[12.5px] font-bold truncate" style={{ color: INK }}>{r.companyName}</div>
                                        <div className="text-[10.5px] mt-0.5" style={{ color: INK4 }}>
                                            {r.ticker}{r.documentDate ? ` · ${getDaysAgo(r.documentDate)}` : ''}
                                        </div>
                                    </div>
                                    <ChevronRight className="w-3.5 h-3.5 shrink-0" style={{ color: INK4 }} />
                                </button>
                            ))}
                            <div className="text-center pt-3">
                                <button onClick={() => navigate('/hiring-signals-us?direction=hiring')}
                                        className="text-[12px] font-semibold text-[#3F57E0] hover:underline">
                                    Browse all US signals →
                                </button>
                            </div>
                        </RailCard>
                    )}
                </aside>
            </div>

            {toast && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2.5 rounded-xl bg-gray-900 text-white text-[13px] font-medium shadow-lg z-50">
                    {toast}
                </div>
            )}

            <CompanyInsightsModal
                open={insightsOpen}
                onClose={() => setInsightsOpen(false)}
                companyName={company.companyName}
                ticker={company.ticker || ticker}
            />

            <ContactsModal
                open={contactsOpen}
                onClose={() => setContactsOpen(false)}
                contacts={allContacts}
                companyName={company.companyName}
                onChooseStrategy={() => setOutreachOpen(true)}
            />

            {/*
                context="signal" gives the hiring-signal playbooks — warming a
                company before the role exists — rather than the job-application
                ones, which is the right frame here: there is no posting yet.
            */}
            <OutreachStrategyModal
                open={outreachOpen}
                onClose={() => setOutreachOpen(false)}
                context="signal"
                companyName={company.companyName}
                contacts={allContacts}
                onActivate={handleActivateStrategy}
                onViewTasks={() => { setOutreachOpen(false); navigate('/dailytasks'); }}
            />

        </div>
    );
};

export default PublicSignalDetailPage;
